import { MotionDetectionWindow, SiteSchedule } from '@hakimo-ui/hakimo/types';
import {
  WEEKDAYS,
  convertToSeconds,
  isValidTime,
  withSeconds,
} from '@hakimo-ui/hakimo/util';

const getEnabledDaysFromDayNames = (dayNames: string[]): boolean[] =>
  dayNames.reduce((acc, day) => {
    const dayIndex = WEEKDAYS.indexOf(day.toUpperCase());
    if (dayIndex >= 0) {
      acc[dayIndex] = true;
    }
    return acc;
  }, new Array(7).fill(false) as boolean[]);

const getDayNamesFromEnabledDays = (enabledDays: boolean[]): string[] =>
  enabledDays.reduce((acc, dayEnabled, index) => {
    if (dayEnabled) {
      acc.push(WEEKDAYS[index]);
    }
    return acc;
  }, [] as string[]);

export const getDefaultSchedule = (): SiteSchedule => ({
  ranges: [{ startTime: '', endTime: '', order: 0 }],
  enabledDays: new Array(7).fill(true),
});

export function getSchedulesFromPayload(
  motionDetectionWindow: MotionDetectionWindow
): SiteSchedule[] {
  return (motionDetectionWindow.dayGroupRanges ?? []).map((dayGroup) => ({
    ranges: dayGroup.ranges.map((range) => ({
      startTime: range.startAtTz,
      endTime: range.endAtTz,
      order: range.order ?? 0,
    })),
    enabledDays: getEnabledDaysFromDayNames(dayGroup.daysOfWeekTz),
  }));
}

export function getPayloadFromSchedules(
  schedules: SiteSchedule[],
  timezone: string
): MotionDetectionWindow {
  return {
    timezone,
    dayGroupRanges: schedules.map((schedule) => ({
      ranges: schedule.ranges.map((range) => ({
        startAtTz: withSeconds(range.startTime),
        endAtTz: withSeconds(range.endTime),
        order: range.order,
      })),
      daysOfWeekTz: getDayNamesFromEnabledDays(schedule.enabledDays),
    })),
  };
}

export const validateAllSchedules = (schedules: SiteSchedule[]) => {
  let isValid = true;
  let message = '';

  for (let i = 0; i < schedules.length; i++) {
    const currentSchedule = schedules[i];
    for (let j = 0; j < currentSchedule.ranges.length; j++) {
      const currentRange = currentSchedule.ranges[j];
      const startTime = currentRange.startTime;
      const endTime = currentRange.endTime;
      if (!isValidTime(startTime) || !isValidTime(endTime)) {
        isValid = false;
        message =
          'Please provide a start time and an end time for every schedule.';
        break;
      }
      const isNoDaySelected = currentSchedule.enabledDays.every(
        (day) => day === false
      );
      if (isNoDaySelected) {
        isValid = false;
        message = 'Ensure at least one day is chosen for each schedule.';
      }
    }

    if (currentSchedule.ranges.length > 1) {
      const sortedRanges = [...currentSchedule.ranges].sort(
        (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
      );
      let prevEnd = undefined;
      for (const range of sortedRanges) {
        const startTime = convertToSeconds(range.startTime);
        const endTime = convertToSeconds(range.endTime);

        if (prevEnd && prevEnd > startTime) {
          isValid = false;
          message =
            'Multiple range overlap. End time of first range should not exceed next range start time';
        }
        prevEnd = endTime;
      }
    }
  }

  return { isValid, message };
};
